import { ComponentProps, useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { api } from 'src/api/root'
import useAuth from 'src/enviroment/hooks/useAuth'
import { toSafeReturnPath } from 'src/enviroment/utils/toSafeReturnPath'
import SignInTemplate from 'src/templates/auth/sign-in/SignInTemplate'
import { ROUTES, STEP_ROUTES } from 'src/types/routes'

export default function SignInPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, user, isLoading: isUserLoading } = useAuth()
  const [isInitializing, setIsInitializing] = useState<boolean>(true)

  const searchParams = new URLSearchParams(window.location.search)
  // Set by ProtectedRoute or an expired-session logout, so a deep link (e.g. the OAuth consent page) survives the sign-in.
  const returnTo: string | undefined = location.state?.from ?? toSafeReturnPath(searchParams.get('from'))
  const invitationToken = searchParams.get('token')
  const invitedEmail = searchParams.get('email')

  const { mutateAsync: signIn, isPending: isSigningIn } = api.auth.login.useMutation()
  const { mutateAsync: joinToTenant } = api.user.currentUser.joinTenant.useMutation()

  const handleSubmit: ComponentProps<typeof SignInTemplate>['onSubmit'] = useCallback(
    async ({ email, password, isRemember }) => {
      try {
        const { user } = await signIn({
          username: email,
          password,
          isRemember,
          invitationToken,
        })

        navigate(returnTo ?? STEP_ROUTES[user.step], { replace: true })
      } catch (error) {
        console.error('Sign in failed:', error)
      }
    },
    [signIn, invitationToken, navigate, returnTo],
  )

  const handleForgotPassword = useCallback(() => {
    navigate(ROUTES.FORGOT_PASSWORD)
  }, [navigate])

  useEffect(() => {
    const handleInitialCheck = async () => {
      if (!isAuthenticated) {
        setIsInitializing(false)
        return
      }

      if (isUserLoading) {
        return
      }

      if (invitationToken) {
        try {
          await joinToTenant({ invitationToken })
          navigate(returnTo ?? ROUTES.PROJECTS, { replace: true })
        } catch (error) {
          setIsInitializing(false)
          return
        }
      } else {
        navigate(returnTo ?? ROUTES.PROJECTS, { replace: true })
      }
      setIsInitializing(false)
    }

    handleInitialCheck()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinToTenant, invitationToken, isAuthenticated, isUserLoading, user])

  if (isInitializing) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900" />
      </div>
    )
  }

  return (
    <SignInTemplate
      onSubmit={handleSubmit}
      isLoading={isSigningIn}
      onForgotPassword={handleForgotPassword}
      defaultEmail={invitedEmail}
    />
  )
}
